// Movimientos bancarios — libro banco por cuenta con saldo corriente.
// Control Bancario → Movimientos → Movimientos bancarios.
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Ban, CheckCircle2, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import {
  CATEGORIA_MOV, Campo, CuentaMini, LibroBanco, Modal, Movimiento, SortTh, cb, cbRpc, fieldCls, hoyISO, label, money, useSort,
} from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const movVacio = { fecha: hoyISO(), tipo: 'debito', monto: 0, concepto: '', referencia: '', categoria: '' };

export function Movimientos() {
  const { t } = useTranslation('bancos');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuentas = [] } = useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
  const [cuentaId, setCuentaId] = useState('');
  const cta = cuentaId || cuentas[0]?.id || '';
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [fCategoria, setFCategoria] = useState('');
  const [fReferencia, setFReferencia] = useState('');
  const [fConcepto, setFConcepto] = useState('');

  const { data, isFetching } = useQuery<LibroBanco>({
    queryKey: ['cb-libro', cta, desde, hasta],
    queryFn: () => cbRpc<LibroBanco>('cb_movimientos', { _cuenta_id: cta, _desde: desde || null, _hasta: hasta || null }),
    enabled: !!cta,
  });
  const movs = useMemo(() => data?.movimientos ?? [], [data]);
  const moneda = data?.cuenta.moneda ?? 'USD';
  const filtrando = !!(fCategoria || fReferencia.trim() || fConcepto.trim());
  const movsFiltrados = useMemo(() => movs.filter((m) =>
    (!fCategoria || (m.categoria ?? '') === fCategoria) &&
    (!fReferencia.trim() || (m.referencia ?? '').toLowerCase().includes(fReferencia.trim().toLowerCase())) &&
    (!fConcepto.trim() || (m.concepto ?? '').toLowerCase().includes(fConcepto.trim().toLowerCase()))
  ), [movs, fCategoria, fReferencia, fConcepto]);
  const getVal = useCallback((m: Movimiento, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return m.fecha;
      case 'concepto': return m.concepto ?? '';
      case 'referencia': return m.referencia ?? '';
      case 'categoria': return label(CATEGORIA_MOV, m.categoria);
      case 'debito': return m.tipo === 'debito' ? Number(m.monto) : null;
      case 'credito': return m.tipo === 'credito' ? Number(m.monto) : null;
      case 'saldo': return Number(m.saldo);
      case 'origen': return m.origen;
      default: return null;
    }
  }, []);
  const sort = useSort(movsFiltrados, getVal);

  // Exportar (CSV / XLS / PDF) — respeta filtros y orden actuales
  const nombreArchivo = `movimientos-bancarios-${fechaLocal()}`;
  const headersExp = [t('movimientos.col_n'), t('movimientos.col_fecha'), t('movimientos.col_concepto'), t('movimientos.col_referencia'), t('movimientos.col_categoria'), t('movimientos.col_debito'), t('movimientos.col_credito'), t('movimientos.col_saldo'), t('movimientos.col_origen')];
  const totDebito = sort.sorted.filter((m) => m.tipo === 'debito').reduce((a, m) => a + Number(m.monto), 0);
  const totCredito = sort.sorted.filter((m) => m.tipo === 'credito').reduce((a, m) => a + Number(m.monto), 0);
  const filasExport: Celda[][] = [
    ...sort.sorted.map((m, i): Celda[] => [
      i + 1, m.fecha, m.concepto ?? '', m.referencia ?? '', label(CATEGORIA_MOV, m.categoria),
      m.tipo === 'debito' ? Number(m.monto) : '', m.tipo === 'credito' ? Number(m.monto) : '', Number(m.saldo), m.origen,
    ]),
    ['', '', '', '', t('movimientos.total'), totDebito, totCredito, Number(data?.saldo_final ?? 0), ''],
  ];

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof movVacio>(movVacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof movVacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (m?: Movimiento) => {
    if (m) { setEditId(m.id); setForm({ fecha: m.fecha, tipo: m.tipo, monto: m.monto, concepto: m.concepto ?? '', referencia: m.referencia ?? '', categoria: m.categoria ?? '' }); }
    else { setEditId(null); setForm(movVacio); }
    setAbierto(true);
  };

  const invalidar = () => { void qc.invalidateQueries({ queryKey: ['cb-libro'] }); void qc.invalidateQueries({ queryKey: ['cb-posicion'] }); };

  const guardar = useMutation({
    mutationFn: () => {
      const body = { cuenta_id: cta, ...form, monto: Number(form.monto) || 0, concepto: form.concepto || null, referencia: form.referencia || null, categoria: form.categoria || null };
      return editId ? cb.update('movimientos', editId, body) : cb.create('movimientos', body);
    },
    onSuccess: () => { toast.success(t('movimientos.guardado')); setAbierto(false); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => cb.update('movimientos', id, { estado: 'anulado' }),
    onSuccess: () => { toast.success(t('movimientos.anulado')); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = useMemo(() => data?.totales ?? { creditos: 0, debitos: 0, n: 0 }, [data]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ArrowLeftRight className="w-5 h-5" /> {t('movimientos.titulo')}</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={nombreArchivo} titulo={t('movimientos.titulo')} headers={headersExp} filas={filasExport}
            colsDerecha={[0, 5, 6, 7]} subtitulo={t('movimientos.sub_export', { fecha: new Date().toLocaleString(LOCALE), n: sort.sorted.length })}
            fmtPDF={(v, c) => ([5, 6, 7].includes(c) ? (v === '' ? '' : money(Number(v), moneda)) : String(v ?? ''))}
            disabled={sort.sorted.length === 0} />
          <Button onClick={() => abrir()} disabled={!cta}><Plus className="w-4 h-4 mr-1" /> {t('movimientos.btn_nuevo')}</Button>
        </div>
      </div>

      <Card>
        <CardContent className="py-3 flex flex-wrap items-end gap-3">
          <Campo label={t('movimientos.f_cuenta')} className="min-w-[220px]">
            <select className={fieldCls} value={cta} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.length === 0 && <option value="">{t('movimientos.sin_cuentas')}</option>}
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
            </select>
          </Campo>
          <Campo label={t('movimientos.f_desde')}><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label={t('movimientos.f_hasta')}><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Campo label={t('movimientos.f_categoria')} className="min-w-[160px]">
            <select className={fieldCls} value={fCategoria} onChange={(e) => setFCategoria(e.target.value)}>
              <option value="">{t('movimientos.todas')}</option>
              {CATEGORIA_MOV.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </Campo>
          <Campo label={t('movimientos.f_referencia')}><input className={fieldCls} value={fReferencia} onChange={(e) => setFReferencia(e.target.value)} placeholder={t('movimientos.ph_ref_filtro')} /></Campo>
          <Campo label={t('movimientos.f_concepto')} className="min-w-[200px]"><input className={fieldCls} value={fConcepto} onChange={(e) => setFConcepto(e.target.value)} placeholder={t('movimientos.ph_concepto')} /></Campo>
          {(desde || hasta || filtrando) && <Button variant="outline" onClick={() => { setDesde(''); setHasta(''); setFCategoria(''); setFReferencia(''); setFConcepto(''); }}>{t('movimientos.limpiar')}</Button>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={t('movimientos.kpi_saldo_inicial')} valor={money(data?.saldo_inicial_periodo, moneda)} />
        <Kpi label={t('movimientos.kpi_creditos')} valor={money(kpis.creditos, moneda)} clase="text-estable" />
        <Kpi label={t('movimientos.kpi_debitos')} valor={money(kpis.debitos, moneda)} clase="text-pendiente" />
        <Kpi label={t('movimientos.kpi_saldo_final')} valor={money(data?.saldo_final, moneda)} fuerte />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('movimientos.libro', { n: filtrando ? t('movimientos.libro_filtrado', { filtrados: movsFiltrados.length, total: kpis.n }) : kpis.n })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right w-12">{t('movimientos.col_n')}</th><SortTh col="fecha" sort={sort}>{t('movimientos.col_fecha')}</SortTh><SortTh col="concepto" sort={sort}>{t('movimientos.col_concepto')}</SortTh><SortTh col="referencia" sort={sort}>{t('movimientos.col_ref')}</SortTh><SortTh col="categoria" sort={sort}>{t('movimientos.col_categoria')}</SortTh><SortTh col="debito" sort={sort} align="right">{t('movimientos.col_debito')}</SortTh><SortTh col="credito" sort={sort} align="right">{t('movimientos.col_credito')}</SortTh><SortTh col="saldo" sort={sort} align="right">{t('movimientos.col_saldo')}</SortTh><SortTh col="origen" sort={sort}>{t('movimientos.col_origen')}</SortTh><th className="py-2 pr-3 text-center">{t('movimientos.col_cont')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && !isFetching && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">{filtrando ? t('movimientos.vacio_filtro') : t('movimientos.vacio_periodo')}</td></tr>}
              {sort.sorted.map((m, i) => (
                <tr key={m.id} className={`border-b last:border-0 hover:bg-muted/40 ${m.estado === 'anulado' ? 'opacity-50 line-through' : ''}`}>
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{m.fecha}</td>
                  <td className="py-2 pr-3">{m.concepto ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{m.referencia ?? '—'}</td>
                  <td className="py-2 pr-3">{label(CATEGORIA_MOV, m.categoria)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-pendiente">{m.tipo === 'debito' ? money(m.monto, moneda) : ''}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-estable">{m.tipo === 'credito' ? money(m.monto, moneda) : ''}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(m.saldo, moneda)}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">{m.origen}</span></td>
                  <td className="py-2 pr-3 text-center">{m.contabilizado ? <CheckCircle2 className="w-4 h-4 text-estable inline" /> : m.estado === 'conciliado' ? <span className="text-xs text-pendiente" title={t('movimientos.conciliado_sin_contab')}>•</span> : ''}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {m.estado === 'registrado' && m.origen === 'manual' && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('movimientos.editar')} onClick={() => abrir(m)}><Pencil className="w-4 h-4" /></button>
                    )}
                    {m.estado === 'registrado' && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('movimientos.anular')} onClick={async () => { if (await dlg.confirmar(t('movimientos.confirmar_anular'), { peligro: true })) anular.mutate(m.id); }}><Ban className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {sort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3 text-right" colSpan={5}>{t('movimientos.totales', { n: sort.sorted.length })}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-pendiente">{money(sort.sorted.filter((m) => m.tipo === 'debito').reduce((a, m) => a + Number(m.monto), 0), moneda)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-estable">{money(sort.sorted.filter((m) => m.tipo === 'credito').reduce((a, m) => a + Number(m.monto), 0), moneda)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data?.saldo_final, moneda)}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? t('movimientos.editar_mov') : t('movimientos.nuevo_mov')} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('movimientos.f_fecha')}><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
              <Campo label={t('movimientos.f_tipo')}>
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  <option value="debito">{t('movimientos.tipo_debito')}</option>
                  <option value="credito">{t('movimientos.tipo_credito')}</option>
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('movimientos.f_monto')}><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
              <Campo label={t('movimientos.f_categoria')}>
                <select className={fieldCls} value={form.categoria} onChange={(e) => set({ categoria: e.target.value })}>
                  <option value="">{t('movimientos.sin_categoria')}</option>
                  {CATEGORIA_MOV.filter((c) => c.v !== 'transferencia').map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label={t('movimientos.f_concepto')}><input className={fieldCls} value={form.concepto} onChange={(e) => set({ concepto: e.target.value })} /></Campo>
            <Campo label={t('movimientos.f_referencia')}><input className={fieldCls} value={form.referencia} onChange={(e) => set({ referencia: e.target.value })} placeholder={t('movimientos.ph_referencia')} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('movimientos.cancelar')}</Button>
              <Button isLoading={guardar.isPending} disabled={!form.fecha || Number(form.monto) <= 0} onClick={() => guardar.mutate()}>{t('movimientos.guardar')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Kpi({ label: l, valor, clase, fuerte }: { label: string; valor: string; clase?: string; fuerte?: boolean }) {
  return (
    <Card><CardContent className="py-3">
      <div className="text-xs text-muted-foreground">{l}</div>
      <div className={`font-mono tabular-nums ${fuerte ? 'text-lg font-semibold' : 'text-base'} ${clase ?? ''}`}>{valor}</div>
    </CardContent></Card>
  );
}
